/**
 * Time-based greeting utility.
 * Prefix copy is resolved from Contentful via standard (non-ADHA) locales:
 * home.goodMorning / home.goodAfternoon / home.goodEvening
 *
 * Contentful supplies the words only ("Good Morning"). We append a comma
 * immediately after that text when a user name is shown. Guest / nameless
 * greetings stay without a comma.
 */

import type { FlatTranslationKey } from '@/core/types/i18n';

export type GreetingType = 'morning' | 'afternoon' | 'evening';

const GREETING_TRANSLATION_KEYS: Record<GreetingType, FlatTranslationKey> = {
  morning: 'home.goodMorning',
  afternoon: 'home.goodAfternoon',
  evening: 'home.goodEvening',
};

/**
 * Get the time-based greeting type based on current hour.
 * Morning / afternoon / evening only — never night.
 */
export function getGreetingType(): GreetingType {
  const hour = new Date().getHours();

  // Morning: 5:00 AM - 11:59 AM
  if (hour >= 5 && hour < 12) {
    return 'morning';
  }
  // Afternoon: 12:00 PM - 4:59 PM
  if (hour >= 12 && hour < 17) {
    return 'afternoon';
  }
  // Evening: 5:00 PM - 4:59 AM
  return 'evening';
}

/**
 * Contentful key for the current time-of-day greeting (non-ADHA locales).
 * Resolve with `t(...)` or prefer `resolveGreetingPrefix(t)` for the full prefix.
 */
export function getGreetingTranslationKey(): FlatTranslationKey {
  return GREETING_TRANSLATION_KEYS[getGreetingType()];
}

/**
 * Contentful greeting words only, e.g. "Good Morning" (no trailing comma).
 * Pass `t` from `useTranslation()` so the value is locale/Contentful-resolved.
 */
export function resolveGreetingPrefix(
  t: (key: FlatTranslationKey) => string,
): string {
  return t(getGreetingTranslationKey()).trim();
}

/**
 * Append "," immediately after the Contentful greeting when a name follows.
 * Guest and empty-name cases keep the prefix unchanged.
 */
export function withGreetingNameComma(
  prefix: string,
  name: string | undefined | null,
): string {
  const text = prefix.trim();
  if (!name?.trim()) {
    return text;
  }
  return `${text},`;
}

/**
 * Named greeting used on ADHA listing (web + mobile):
 * "Good Morning, FirstName!"
 */
export function formatNamedGreeting(
  dynamicPrefix: string,
  userDisplayName: string,
): string {
  const greetingName = userDisplayName.trim().split(/\s+/)[0] || '';
  const prefix = withGreetingNameComma(dynamicPrefix, greetingName);
  if (greetingName) {
    return `${prefix} ${greetingName}!`;
  }
  return `${prefix}!`;
}
